/**
 * EditorContent (Babylon) — the editor's scene content, built on web-core's
 * CreatorContent and driven by the app's content runner (AppContent.setContent):
 * the app calls init(engine, scene, camera, rootNode) on set, render(dt) each
 * frame, and dispose() on teardown.
 *
 * Replaces the old EditorScene: a transform node holding the scene's elements
 * (EditorContentElement / CreatorElement) plus a FloorMarker, with the same
 * add/remove/update diff in handleElementChanges. Inherits CreatorContent's
 * element registry (elements / getElement / removeElement); adds the editor
 * concerns CreatorContent doesn't model — the scene-node transform from config,
 * the floor marker, the element diff, and post-bootstrap auto-select.
 */

import { Camera, Engine, TransformNode, Vector3, type Scene } from 'babylonjs';
import CreatorContent from '@webcore/core/creator/creator.content';
import { CreatorArtworkWorldTargetType, type CreatorArtworkWorld } from '@webcore/core/creator/types';
import { FloorMarker } from '../scene/FloorMarker';
import { quaternionFromEulerDegreesXYZ } from '../utils/BabylonEditorUtils';
import { AssetTypes } from '@shared/constants';
import EditorPanelElement from './EditorPanelElement';
import EditorModelElement from './EditorModelElement';
import type { EditorElementLike } from './EditorContentElement';
import type { ElementStateData } from './EditorContentTypes';
import type { EditorState, ElementState } from '@shared/EditorState';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import type { AssetManager } from '../assets/AssetManager';

export interface SceneConfigData {
  sceneID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

// CreatorContent is artwork-driven; the editor drives it from EditorState +
// AssetManager instead, so we pass an empty artwork and never call its build().
const EMPTY_ARTWORK: CreatorArtworkWorld = {
  type: CreatorArtworkWorldTargetType,
  id: '',
  files: [],
  sceneConfig: { version: 1, assets: [], scenes: [] },
};

export class EditorContent extends CreatorContent {
  readonly sceneID: string;

  private nodeConfig: SceneConfigData;
  private state: EditorState;
  private events: EventBus<EditorEvents>;
  private assetManager: AssetManager;
  private marker: FloorMarker | null = null;
  /** Auto-select a freshly added element (post-bootstrap). */
  private onAutoSelect: (sceneID: string) => void;

  constructor(
    config: SceneConfigData,
    state: EditorState,
    events: EventBus<EditorEvents>,
    assetManager: AssetManager,
    onAutoSelect: (sceneID: string) => void,
  ) {
    super(EMPTY_ARTWORK, {});
    this.sceneID = config.sceneID;
    this.nodeConfig = config;
    this.state = state;
    this.events = events;
    this.assetManager = assetManager;
    this.onAutoSelect = onAutoSelect;
  }

  // Content runner entry point (called by AppContent.setContent). Sets up the
  // scene transform node + floor marker; does NOT run CreatorContent.build()
  // (the editor drives elements via handleElementChanges).
  public override async init(engine: Engine, scene: Scene, camera: Camera, rootNode?: TransformNode): Promise<void> {
    this.engine = engine;
    this.scene = scene;
    this.camera = camera;
    this.rootNode = rootNode;

    const node = new TransformNode(`Scene_${this.nodeConfig.sceneID}`, scene);
    node.position = new Vector3(this.nodeConfig.position[0], this.nodeConfig.position[1], this.nodeConfig.position[2]);
    node.rotationQuaternion = quaternionFromEulerDegreesXYZ(
      this.nodeConfig.rotation[0], this.nodeConfig.rotation[1], this.nodeConfig.rotation[2],
    );
    node.scaling = new Vector3(this.nodeConfig.scale[0], this.nodeConfig.scale[1], this.nodeConfig.scale[2]);
    this.node = node;

    this.marker = new FloorMarker(node, scene, this.events);
  }

  /** Public accessor for the scene node (CreatorContent.node is protected). */
  get sceneNode(): TransformNode {
    return this.node!;
  }

  handleElementChanges(newElements: ElementState[]): void {
    const old = this.elements as unknown as EditorElementLike[];
    const toRemove = old.filter((p) => !newElements.some((e) => e.sceneID === p.sceneID));
    const toAdd = newElements.filter((e) => !old.some((p) => p.sceneID === e.sceneID));
    const toUpdate = newElements.filter((e) => old.some((p) => p.sceneID === e.sceneID));

    for (const p of toRemove) this.removeElement(p.sceneID);

    for (const el of toAdd) this.addEditorElement(el);

    // Auto-select a single element added after bootstrap.
    if (toAdd.length === 1 && this.state.bootstrapComplete) {
      this.state.setSelectedElements([]);
      this.onAutoSelect(toAdd[0].sceneID);
    }

    for (const el of toUpdate) {
      const element = this.getEditorElement(el.sceneID);
      if (element) {
        // Selected element updates apply immediately (user may be manipulating it).
        element.setTransform(
          { position: el.position, rotation: el.rotation, scale: el.scale },
          !element.isSelected,
        );
      }
    }
  }

  private addEditorElement(el: ElementState): void {
    const data: ElementStateData = {
      sceneID: el.sceneID,
      assetID: el.assetID,
      parentID: el.parentID,
      position: el.position,
      rotation: el.rotation,
      scale: el.scale,
    };
    const isModel = el.assetID
      ? this.assetManager.getAssetType(el.assetID) === AssetTypes.GLB
      : false;
    const element = isModel
      ? new EditorModelElement(this.node!, data, this.scene!, this.state, this.events, this.assetManager)
      : new EditorPanelElement(this.node!, data, this.scene!, this.state, this.events, this.assetManager);
    this.elements.push(element);
    void element.build(); // async load (texture / GLB instance); fire-and-forget
  }

  getEditorElement(sceneID: string): EditorElementLike | undefined {
    return this.getElement(sceneID) as unknown as EditorElementLike | undefined;
  }

  /** Alias used by the Editor (mirrors the old EditorScene API). */
  getElementById(sceneID: string): EditorElementLike | undefined {
    return this.getEditorElement(sceneID);
  }

  // Per-frame hook (driven by AppContent's render runner). Elements currently do
  // no per-frame work (the gizmo follows the anchor; animations self-advance),
  // but this keeps the hook wired for future needs.
  public override render(_timeDelta = 0): void {
    for (const element of this.elements as unknown as EditorElementLike[]) element.update();
  }

  // Full override: do NOT call super.dispose() — the editor's AssetManager owns
  // the shared model cache (disposeModelCache), and there is no CreatorContent grid.
  public override dispose(): void {
    for (const element of this.elements) element.dispose();
    this.elements = [];
    this.marker?.cleanup();
    this.marker = null;
    this.node?.dispose();
    this.node = undefined;
  }
}

export default EditorContent;
